import type { Dispatch, SetStateAction } from 'react';
import { getNextApiRoute, typedFetch } from '@libs/common-next';
import { getCountry, HttpMethod, isLocaleCA } from '@libs/core';
import { Confidence } from './types';
import type {
    ExperianFormatAddressResponse,
    FormProps,
    ValidatedFormProps,
    InteractionRequiredResponse,
    StreetPartialResponse,
    ExperianAddress,
    Suggestion,
    ExperianResponseLayout,
    ExperianAddressLayout,
    ExperianResponse,
    InteractionRequiredResponseLayout,
    VerifiedMatchResponse,
} from './types';
import type { AddressFormInterface } from '../AddressForm';

const addressVerificationMessages: Record<Confidence, string> = {
    [Confidence.interactionRequired]:
        'Please check your original address. Use the verified address to avoid delivery issues.',
    [Confidence.noMatches]:
        'We could not verify your address. Check for any errors or missing information.',
    [Confidence.premisesPartial]:
        'Please check your apartment or suite number. Using the original address may cause delivery issues.',
    [Confidence.streetPartial]:
        'Please check your street number or name. Using the original address may cause delivery issues.',
    [Confidence.verifiedMatch]: 'Verified',
    [Confidence.multipleMatches]:
        'We found more than one match for your address. To proceed, please choose one of the options below.',
};

export const getAddressVerificationMessage = (type: Confidence): string =>
    addressVerificationMessages[type] ||
    'Unknown verification status. Please check your address.';

export const formatAddress = (formAddress: FormProps['address']) => {
    const { streetAddress, zipCode, state, city, apt } = formAddress;

    return {
        addressType: 'Home',
        streetLine1: streetAddress,
        zipPostalCode: zipCode,
        stateProvinceAbbreviation: state,
        city,
        countryAbbreviation: getCountry(),
        streetLine2: apt,
    };
};

export const extractAddressValuesLayout = (
    address: ExperianAddressLayout['address']
) => {
    console.log('entered here as the extracting the address values');
    const {
        'address.addressLine1': addressLine1,
        'address.addressLine2': addressLine2 = '',
        'address.locality': city,
        'address.province': state,
        'address.postalCode': zipCode,
    } = address;

    return {
        streetAddress: addressLine1,
        apt: addressLine2,
        zipCode: isLocaleCA() ? zipCode : zipCode?.split('-')[0],
        state,
        city,
    };
};

export const handleInteractionRequiredLayout = (
    values: FormProps,
    addressValidationResponse: ExperianResponseLayout,
    setValidatedFormValues: Dispatch<
        SetStateAction<ValidatedFormProps[] | undefined>
    >,
    openModal: () => void
) => {
    if (addressValidationResponse.result) {
        const newValidatedAddress = extractAddressValuesLayout(
            (addressValidationResponse as InteractionRequiredResponseLayout)
                .result?.addresses_formatted[0]?.address
        );
        setValidatedFormValues([
            {
                ...values,
                address: newValidatedAddress,
                gak: (
                    addressValidationResponse as InteractionRequiredResponseLayout
                ).result.global_address_key,
            },
        ]);
        openModal();
    }
};

/**
 * Helper function to process a single suggestion.
 *
 * This function extracts relevant data from a suggestion, including the Global Address Key (GAK), picklist display, and postcode.
 * It uses the `parseAddressFromPicklist` helper function to parse and format the address correctly.
 *
 * @param suggestion - A single suggestion object returned by the Experian API.
 * @param values - The current form values, which are used to create the validated address that will be displayed to the user.
 * @returns A `ValidatedFormProps` object representing the validated address, or null if the suggestion is invalid.
 *
 * **Key Details**:
 * - The method processes each suggestion to prepare it for display as a suggestion to the user.
 * - Along with the validated address, the Global Address Key (GAK) is also saved. The GAK is critical for making subsequent calls to the
 *   `formatSelectedAddress` function.
 * - The `formatSelectedAddress` function formats the address according to predefined layouts:
 *   - `EDQ NA SFCC 5-Line Default` for U.S. addresses.
 *   - `Petsmart CAN` for Canadian addresses.
 */

const processSuggestion = (
    suggestion: Suggestion,
    values: FormProps
): ValidatedFormProps | null => {
    const suggestionKey = suggestion.global_address_key; // Extract the Global Address Key (GAK)
    const additionalAttributes = suggestion.additional_attributes;

    // Extract the picklist display value and postcode from additional attributes
    const picklistDisplay = additionalAttributes.find(
        attr => attr.name === 'picklist_display'
    )?.value;
    const postcode = additionalAttributes.find(attr => attr.name === 'postcode')
        ?.value;

    // If picklistDisplay and postcode exist, parse the address
    if (picklistDisplay && postcode) {
        const parts = picklistDisplay.trim().split(',');
        const streetAddress =
            parts.length > 1 ? parts[parts.length - 2].trim() : ''; // Extract street address
        const cityStatePart =
            parts.length > 0 ? parts[parts.length - 1].trim() : ''; // Extract city and state part
        const [state, ...cityParts] = cityStatePart.split(' ').reverse(); // Reverse split to separate state and city
        const city = [...cityParts].reverse().join(' '); // Join the reversed city parts back into a single string

        const addressSuggesstion = {
            streetAddress,
            apt: '',
            city,
            state,
            zipCode: isLocaleCA() ? postcode : postcode?.split('-')[0],
        };

        // If the address is valid, create and return a ValidatedFormProps object
        if (addressSuggesstion) {
            return {
                ...values,
                address: addressSuggesstion,
                gak: suggestionKey, // Include the Global Address Key in the returned object
            };
        }
    }

    return null; // Return null if address parsing fails
};

/**
 * Handles multiple address suggestions and presents them to the user.
 *
 * This function processes the suggestions from the Experian response, extracts relevant address data, and stores the validated addresses in the state.
 * It also opens a modal to display the suggestions to the user for selection.
 *
 * **Workflow**:
 * 1. Extract the suggestions from the Experian response.
 * 2. For each suggestion, process it and create a `ValidatedFormProps` object if valid.
 * 3. Add the valid addresses to the `newValidatedAddresses` array.
 * 4. Update the form state with the validated addresses and open the modal for the user to choose an address.
 *
 * @param values - The form values to be used in the validated address.
 * @param addressValidationResponse - The response from the Experian address validation API, which contains the suggestions.
 * @param setValidatedFormValues - The function to update the validated form values in the state.
 * @param openModal - The function to open the modal displaying the address suggestions.
 */
export const handleMultipleMatchesLayout = async (
    values: FormProps,
    addressValidationResponse: ExperianResponseLayout,
    setValidatedFormValues: Dispatch<
        SetStateAction<ValidatedFormProps[] | undefined>
    >,
    openModal: () => void
) => {
    // Check if the response contains suggestions (i.e., multiple matches were found)
    if ('suggestions' in addressValidationResponse.result) {
        const { suggestions } = addressValidationResponse.result; // Extract the suggestions array
        const newValidatedAddresses: ValidatedFormProps[] = []; // Initialize an array to store the validated addresses

        // Iterate over each suggestion and process it
        suggestions.forEach(suggestion => {
            const newValidatedAddress = processSuggestion(suggestion, values); // Process each suggestion

            // If the suggestion is valid, add it to the list of validated addresses
            if (newValidatedAddress) {
                newValidatedAddresses.push(newValidatedAddress);
            }
        });

        // Update the state with the new validated addresses and open the modal
        setValidatedFormValues(newValidatedAddresses);
        openModal();
    }
};

/**
 * Makes a call to the Experian API to validate an address and returns the confidence level. This call
 * is triggered when the user fills out an address on a UI form and clicks the 'Save' button.
 *
 * This function sends an address to the Experian API for validation. The address is formatted by combining the street address, apartment number (if provided), city, state, and ZIP code.
 * Additionally, a layout value is included in the request body to map the address according to specific configurations.
 *
 * - **Layouts**: The layout helps the Experian API map the address properly based on predefined configurations. The layout used for U.S. addresses is `EDQ NA SFCC 5-Line Default`,
 * and for Canadian addresses, the layout is `Petsmart CAN`. These layouts determine how the address is structured and formatted in the response.
 *
 * In the response, the validated address is returned in a formatted structure that includes fields like address line 1, address line 2, locality, province, and postal code.
 * For example:
 * ```json
 * "addresses_formatted": [
 *   {
 *     "layout_name": "EDQ NA SFCC 5-Line Default",
 *     "address": {
 *       "address.addressLine1": "9 Porter Cir",
 *       "address.addressLine2": "",
 *       "address.locality": "Cambridge",
 *       "address.province": "MA",
 *       "address.postalCode": "02140-2106"
 *     }
 *   }
 * ]
 * ```
 *
 * **Layouts Configuration**:
 * - The `EDQ NA SFCC 5-Line Default` layout is used for U.S. addresses, ensuring that addresses are mapped and returned in the appropriate format for the U.S. region.
 * - The `Petsmart CAN` layout is used for Canadian addresses to ensure proper mapping for that region.
 * - New layouts can be custom-created as per specific requirements. However, the currently used layouts (`EDQ NA SFCC 5-Line Default` for the U.S. and `Petsmart CAN` for Canada)
 * are pre-defined and have been in use in legacy `/account` pages.
 *
 * @param addressValues Optional address details to validate.
 * @returns The response from the Experian API containing the validated address or `null` if validation fails.
 */
export async function validateExperianAddressLayout(
    addressValues?: AddressFormInterface
): Promise<ExperianResponseLayout | null> {
    if (!addressValues) {
        return null;
    }
    const aptPart = addressValues.apt ? `, ${addressValues.apt}` : '';
    const addressToValidate = `${addressValues.streetAddress}${aptPart}, ${addressValues.city}, ${addressValues.state}, ${addressValues.zipCode}`;

    try {
        return await typedFetch<ExperianResponseLayout | null>(
            getNextApiRoute(`address/validate-address-layout`),
            {
                method: HttpMethod.POST,
                body: JSON.stringify({
                    address: addressToValidate,
                    layouts: isLocaleCA()
                        ? 'Petsmart CAN'
                        : 'EDQ NA SFCC 5-Line Default',
                }),
                headers: {
                    'content-type': 'application/json',
                },
            }
        );
    } catch (e) {
        return null;
    }
}

/**
 * This method is used when the user selects one of the suggestions in the modal
 * displaying multiple matches suggestions.
 *
 * The corresponding GAK (Global Address Key) associated with the selected suggestion
 * is extracted, and an API call is made to format the address correctly. The formatting
 * is based on the selected layout, which depends on the locale:
 * - `Petsmart CAN` for Canada.
 * - `EDQ NA SFCC 5-Line Default` for the United States.
 */
export async function formatSelectedAddressLayout(
    globalAddressKeyInput: string
) {
    try {
        const formatAddressResponse =
            await typedFetch<ExperianFormatAddressResponse | null>(
                getNextApiRoute(`address/format-address-layout`),
                {
                    method: HttpMethod.POST,
                    body: JSON.stringify({
                        globalAddressKeyInput,
                        layouts: isLocaleCA()
                            ? 'Petsmart CAN'
                            : 'EDQ NA SFCC 5-Line Default',
                    }),
                    headers: {
                        'content-type': 'application/json',
                    },
                }
            );

        if (formatAddressResponse) {
            return extractAddressValuesLayout(
                formatAddressResponse.result?.addresses_formatted[0]?.address
            );
        }
        return null;
    } catch (e) {
        return null;
    }
}

/**
 * Extracts the postal code from the Experian response and compares it with user input
 * to determine if the postal code should be highlighted in a modal in case of a mismatch.
 */
export const extractZipCodeFromResponseLayout = (
    addressValidationResponse?: ExperianResponseLayout
): string | null => {
    if (!addressValidationResponse) {
        return null;
    }

    const { confidence } = addressValidationResponse.result;

    switch (confidence) {
        case 'Street partial':
        case 'Premises partial': {
            const [suggestion] = (
                addressValidationResponse as StreetPartialResponse
            ).result.suggestions;

            const zipCode = suggestion.additional_attributes.find(
                attr => attr.name === 'postcode'
            )?.value;

            return zipCode?.split('-')[0] ?? null;
        }

        case 'Interaction required': {
            const zipCode = (
                addressValidationResponse as InteractionRequiredResponseLayout
            ).result?.addresses_formatted[0].address['address.postalCode'];
            return isLocaleCA() ? zipCode : zipCode?.split('-')[0];
        }

        default:
            console.warn('Unsupported confidence level:', confidence);
            return null;
    }
};

export const handleAddressValidationErrorsLayout = async (
    values: FormProps,
    addressValidationResponse: ExperianResponseLayout,
    setFormValues: (prevState: FormProps) => void,
    setValidatedFormValues: Dispatch<
        SetStateAction<ValidatedFormProps[] | undefined>
    >,
    openModal: () => void
) => {
    const confidenceLevel = addressValidationResponse?.result.confidence;

    switch (confidenceLevel) {
        case Confidence.streetPartial:
        case Confidence.premisesPartial:
            openModal();
            break;
        case Confidence.interactionRequired:
            handleInteractionRequiredLayout(
                values,
                addressValidationResponse,
                setValidatedFormValues,
                openModal
            );
            break;
        case Confidence.multipleMatches:
            await handleMultipleMatchesLayout(
                values,
                addressValidationResponse,
                setValidatedFormValues,
                openModal
            );
            break;
        case Confidence.noMatches:
            setFormValues({ ...values });
            openModal();
            break;
        default:
            console.log('Unknown confidence level');
    }
};

/**
 * The old methods below are currently used for address validation and for handling various response types,
 * such as "Interaction Required," "Multiple Matches," and "Partial Responses." These methods do not utilize
 * the additional layout mapping, which ensures addresses are formatted properly to correspond with the desired
 * layout lines.
 *
 * Once the CPC implements the approach using the updated methods (which handle layout mapping effectively),
 * the methods below can be removed.
 *
 *
 * - Remove these old methods (below this comment) after CPC adopts the new approach with layout mapping.
 * - Delete the corresponding test cases associated with these methods as they will no longer be relevant.
 */
export const extractAddressValues = (
    formattedAddress: ExperianAddress,
    buildingFirmNameCorrected?: boolean,
    isVerifiedMatch = false
) => {
    const {
        address_line_1: addressLine1,
        address_line_2: addressLine2 = '',
        address_line_3: addressLine3 = '',
        locality: city,
        region: state,
        postal_code: zipCode,
    } = formattedAddress as ExperianAddress;

    if (isVerifiedMatch) {
        return {
            streetAddress: buildingFirmNameCorrected
                ? addressLine2
                : addressLine1,
            apt: buildingFirmNameCorrected ? addressLine3 : addressLine2,
            zipCode: isLocaleCA() ? zipCode : zipCode?.split('-')[0],
            state,
            city,
        };
    }

    return {
        streetAddress: addressLine1,
        apt: addressLine2,
        zipCode: isLocaleCA() ? zipCode : zipCode?.split('-')[0],
        state,
        city,
    };
};

export const handleMultipleMatches = async (
    values: FormProps,
    addressValidationResponse: ExperianResponse,
    setValidatedFormValues: Dispatch<
        SetStateAction<ValidatedFormProps[] | undefined>
    >,
    openModal: () => void
) => {
    if ('suggestions' in addressValidationResponse.result) {
        const { suggestions } = addressValidationResponse.result;

        const newValidatedAddresses: ValidatedFormProps[] = [];

        suggestions.forEach(suggestion => {
            const suggestionKey = suggestion.global_address_key;
            const displayText = suggestion.text;

            const additionalAttributes = suggestion.additional_attributes;
            const picklistDisplay = additionalAttributes.find(
                attr => attr.name === 'picklist_display'
            )?.value;
            let postcode = additionalAttributes.find(
                attr => attr.name === 'postcode'
            )?.value;

            if (picklistDisplay && postcode) {
                const streetNames = displayText.trim().split(',');
                const { streetAddress, apt } =
                    streetNames.length > 2
                        ? { streetAddress: streetNames[0], apt: streetNames[1] }
                        : { streetAddress: streetNames[0], apt: '' };

                const [_, cityState] = picklistDisplay.split(',');
                const cityStateParts = cityState.trim().split(' ');

                const state = cityStateParts.pop() ?? '';
                const city = cityStateParts.join(' ');

                postcode = isLocaleCA() ? postcode : postcode?.split('-')[0];
                const newValidatedAddress = {
                    ...values,
                    address: {
                        streetAddress,
                        city,
                        state,
                        zipCode: postcode,
                        apt,
                    },
                    gak: suggestionKey,
                };

                newValidatedAddresses.push(newValidatedAddress);
            }
        });

        setValidatedFormValues(newValidatedAddresses);
        openModal();
    }
};

export const handleInteractionRequired = (
    values: FormProps,
    addressValidationResponse: ExperianResponse,
    setValidatedFormValues: Dispatch<
        SetStateAction<ValidatedFormProps[] | undefined>
    >,
    openModal: () => void
) => {
    if (addressValidationResponse.result) {
        const newValidatedAddress = extractAddressValues(
            (addressValidationResponse as InteractionRequiredResponse).result
                .address
        );
        setValidatedFormValues([
            {
                ...values,
                address: newValidatedAddress,
                gak: (addressValidationResponse as InteractionRequiredResponse)
                    .result.global_address_key,
            },
        ]);
        openModal();
    }
};

export const handleAddressValidationErrors = async (
    values: FormProps,
    addressValidationResponse: ExperianResponse,
    setFormValues: (prevState: FormProps) => void,
    setValidatedFormValues: Dispatch<
        SetStateAction<ValidatedFormProps[] | undefined>
    >,
    openModal: () => void
) => {
    const confidenceLevel = addressValidationResponse?.result.confidence;

    switch (confidenceLevel) {
        case Confidence.streetPartial:
        case Confidence.premisesPartial:
            openModal();
            break;
        case Confidence.interactionRequired:
            handleInteractionRequired(
                values,
                addressValidationResponse,
                setValidatedFormValues,
                openModal
            );
            break;
        case Confidence.multipleMatches:
            await handleMultipleMatches(
                values,
                addressValidationResponse,
                setValidatedFormValues,
                openModal
            );
            break;
        case Confidence.noMatches:
            setFormValues({ ...values });
            openModal();
            break;
        default:
            console.log('Unknown confidence level');
    }
};

export async function validateExperianAddress(
    addressValues?: AddressFormInterface
): Promise<ExperianResponse | null> {
    if (!addressValues) {
        return null;
    }
    const aptPart = addressValues.apt ? `, ${addressValues.apt}` : '';
    const addressToValidate = `${addressValues.streetAddress}${aptPart}, ${addressValues.city}, ${addressValues.state}, ${addressValues.zipCode}`;

    try {
        return await typedFetch<ExperianResponse | null>(
            getNextApiRoute(`address/validate-address-layout`),
            {
                method: HttpMethod.POST,
                body: JSON.stringify({
                    address: addressToValidate,
                }),
                headers: {
                    'content-type': 'application/json',
                },
            }
        );
    } catch (e) {
        return null;
    }
}

export async function formatSelectedAddress(globalAddressKeyInput: string) {
    try {
        const formatAddressResponse = await typedFetch<ExperianResponse | null>(
            getNextApiRoute(
                `address/format-address?globalAddressKeyInput=${globalAddressKeyInput}`
            ),
            {
                method: HttpMethod.GET,
                headers: {
                    'content-type': 'application/json',
                },
            }
        );

        if (formatAddressResponse) {
            return extractAddressValues(
                (formatAddressResponse as VerifiedMatchResponse).result.address
            );
        }
        return null;
    } catch (e) {
        return null;
    }
}

export const extractZipCodeFromResponse = (
    addressValidationResponse?: ExperianResponse | ExperianResponseLayout
): string | null => {
    if (!addressValidationResponse) {
        return null;
    }

    const { confidence } = addressValidationResponse.result;

    switch (confidence) {
        case 'Street partial':
        case 'Premises partial': {
            const [suggestion] = (
                addressValidationResponse as StreetPartialResponse
            ).result.suggestions;

            const zipCode = suggestion.additional_attributes.find(
                attr => attr.name === 'postcode'
            )?.value;

            return zipCode?.split('-')[0] || null;
        }

        case 'Interaction required': {
            const zipCode = (
                addressValidationResponse as InteractionRequiredResponse
            ).result.address.postal_code;
            return isLocaleCA() ? zipCode : zipCode?.split('-')[0];
        }

        default:
            console.warn('Unsupported confidence level:', confidence);
            return null;
    }
};
